import React from "react";
import { useCurrentFrame } from "remotion";
import * as THREE from "three";

export const ZuraChibi3DHost: React.FC<{
  frame: number;
  jawOpen: number; // 0.0 - 1.0 from real audio RMS
}> = ({ frame, jawOpen }) => {
  const breathe = Math.sin(frame * 0.08) * 0.02;
  const bobY = Math.sin(frame * 0.1) * 0.015;
  const headTilt = Math.sin(frame * 0.05) * 0.04;
  
  // Blink logic
  const isBlink = (frame % 100) > 92;
  const eyeScaleY = isBlink ? 0.1 : 1.0;

  // Materials for Zura Chibi
  const skinMaterial = new THREE.MeshStandardMaterial({
    color: "#FFE0BD",
    roughness: 0.45,
    metalness: 0.05,
  });

  const hairMaterial = new THREE.MeshStandardMaterial({
    color: "#18181B",
    roughness: 0.65,
    metalness: 0.1,
  });

  const hoodieMaterial = new THREE.MeshStandardMaterial({
    color: "#0F1015",
    roughness: 0.6,
    metalness: 0.1,
  });

  const logoOrangeMaterial = new THREE.MeshStandardMaterial({
    color: "#FF6B00",
    emissive: new THREE.Color("#FF5500"),
    emissiveIntensity: 1.2,
    roughness: 0.2,
  });

  const glassesFrameMaterial = new THREE.MeshStandardMaterial({
    color: "#111216",
    roughness: 0.2,
    metalness: 0.8,
  });

  const glassesLensMaterial = new THREE.MeshPhysicalMaterial({
    color: "#0A0B10",
    roughness: 0.1,
    transmission: 0.6,
    transparent: true,
    opacity: 0.85,
  });

  const mouthInnerMaterial = new THREE.MeshStandardMaterial({
    color: "#3B0D14",
    roughness: 0.3,
  });

  const teethMaterial = new THREE.MeshStandardMaterial({
    color: "#F4F4F5",
    roughness: 0.2,
  });

  const tongueMaterial = new THREE.MeshStandardMaterial({
    color: "#F43F5E",
    roughness: 0.2,
  });

  return (
    <group position={[0, -0.4 + bobY, 0]} rotation={[0, headTilt - 0.25, 0]}>
      {/* 1. TORSO (HOODIE HITAM) */}
      <mesh material={hoodieMaterial} position={[0, -0.85, 0]} castShadow>
        <cylinderGeometry args={[0.42, 0.52, 0.85, 32]} />
      </mesh>
      
      {/* Sablon Logo {Z} Oranye di Dada Kiri */}
      <mesh material={logoOrangeMaterial} position={[-0.14, -0.75, 0.43]} rotation={[0, 0, 0]}>
        <boxGeometry args={[0.12, 0.12, 0.02]} />
      </mesh>

      {/* Tali Hoodie */}
      <mesh material={logoOrangeMaterial} position={[-0.08, -0.9, 0.45]}>
        <cylinderGeometry args={[0.015, 0.015, 0.25, 8]} />
      </mesh>
      <mesh material={logoOrangeMaterial} position={[0.08, -0.9, 0.45]}>
        <cylinderGeometry args={[0.015, 0.015, 0.25, 8]} />
      </mesh>

      {/* 2. LEHER */}
      <mesh material={skinMaterial} position={[0, -0.38, 0]}>
        <cylinderGeometry args={[0.18, 0.2, 0.18, 24]} />
      </mesh>

      {/* 3. KEPALA CHIBI ZURA (ROUND STYLIZED) */}
      <group position={[0, 0.18 + breathe, 0]}>
        <mesh material={skinMaterial} castShadow>
          <sphereGeometry args={[0.62, 36, 36]} />
        </mesh>

        {/* Rambut Acak / Messy Hair Anime */}
        <group position={[0, 0.18, -0.05]}>
          <mesh material={hairMaterial} castShadow>
            <sphereGeometry args={[0.66, 32, 32]} />
          </mesh>
          {/* Spikes / Poni Depan */}
          {[-0.35, -0.18, 0.0, 0.18, 0.35].map((x, i) => (
            <mesh
              key={i}
              material={hairMaterial}
              position={[x, 0.42, 0.48]}
              rotation={[0.35, 0, x * 0.8]}
            >
              <coneGeometry args={[0.12, 0.35, 12]} />
            </mesh>
          ))}
        </group>

        {/* Kacamata Cerdas Tipis */}
        <group position={[0, 0.05, 0.58]}>
          {/* Frame Lensa Kiri & Kanan */}
          <mesh material={glassesFrameMaterial} position={[-0.22, 0, 0]}>
            <torusGeometry args={[0.14, 0.018, 12, 32]} />
          </mesh>
          <mesh material={glassesFrameMaterial} position={[0.22, 0, 0]}>
            <torusGeometry args={[0.14, 0.018, 12, 32]} />
          </mesh>
          {/* Bridge Hidung */}
          <mesh material={glassesFrameMaterial} position={[0, 0.04, 0]}>
            <boxGeometry args={[0.14, 0.02, 0.02]} />
          </mesh>
          {/* Kaca Lensa */}
          <mesh material={glassesLensMaterial} position={[-0.22, 0, 0]}>
            <cylinderGeometry args={[0.13, 0.13, 0.01, 24]} />
          </mesh>
          <mesh material={glassesLensMaterial} position={[0.22, 0, 0]}>
            <cylinderGeometry args={[0.13, 0.13, 0.01, 24]} />
          </mesh>
        </group>

        {/* Mata Anime di Balik Kacamata */}
        <group position={[-0.22, 0.05, 0.55]} scale={[1, eyeScaleY, 1]}>
          <mesh material={hairMaterial}>
            <sphereGeometry args={[0.075, 16, 16]} />
          </mesh>
        </group>
        <group position={[0.22, 0.05, 0.55]} scale={[1, eyeScaleY, 1]}>
          <mesh material={hairMaterial}>
            <sphereGeometry args={[0.075, 16, 16]} />
          </mesh>
        </group>

        {/* Hidung Kecil */}
        <mesh material={skinMaterial} position={[0, -0.06, 0.62]}>
          <sphereGeometry args={[0.035, 12, 12]} />
        </mesh>

        {/* 4. REAL 3D RIGGED MOUTH (LIP-SYNC DENGAN RONGGA MULUT 3D) */}
        {jawOpen > 0.05 ? (
          <group position={[0, -0.22, 0.58]} scale={[1, 0.4 + jawOpen * 0.9, 1]}>
            {/* Rongga Mulut Dalam 3D */}
            <mesh material={mouthInnerMaterial}>
              <sphereGeometry args={[0.12, 24, 24]} />
            </mesh>
            {/* Gigi Atas */}
            <mesh material={teethMaterial} position={[0, 0.06, 0.04]}>
              <boxGeometry args={[0.12, 0.03, 0.04]} />
            </mesh>
            {/* Lidah */}
            <mesh material={tongueMaterial} position={[0, -0.05, 0.03]}>
              <sphereGeometry args={[0.06, 16, 16]} />
            </mesh>
          </group>
        ) : (
          /* Senyuman Tipis Netral Saat Diam */
          <mesh
            material={mouthInnerMaterial}
            position={[0, -0.21, 0.60]}
            rotation={[0, 0, 0]}
            scale={[0.08, 0.015, 0.02]}
          >
            <boxGeometry args={[1, 1, 1]} />
          </mesh>
        )}
      </group>
    </group>
  );
};
